/**
 * Onboarding — chunk 5, step 5.
 *
 * Contract mirror: labourcore-api/src/modules/onboarding/.
 *
 * ── Two things were broken here, and one of them was a hole ────────────────
 *
 * THE DRAFT HAS NEVER BEEN RESTORED. The read was, in full:
 *
 *     supabase.from("onboarding_drafts").select("role, step, data").maybeSingle()
 *
 * There is no user filter. Not a wrong one — none at all. The comment above it
 * said "one row per user, own-row RLS"; RLS on that table is inert, because it
 * is disabled and the connection role holds BYPASSRLS.
 *
 * `onboarding_drafts` holds one row per user and fifteen users have started
 * the wizard, so `maybeSingle()` met fifteen rows where it expected at most
 * one — an error, not a row. **The wizard saves on every keystroke and reloads
 * to an empty form.** Anyone who closed the tab halfway has retyped it.
 *
 * The failure that did not happen is the more interesting one: with exactly
 * one draft in the table, that call would have loaded a stranger's
 * half-finished onboarding — their company name, their trades, their contact
 * details — into somebody else's wizard. The row count is the only thing that
 * stopped it.
 *
 * THE WIZARD PICKED ITS VARIANT FROM THE WRONG FIELD. `user.role` is
 * `profiles.role` — one marketplace side stored against a PERSON — and
 * migration 029 dropped it. The variant now comes from the acting company's
 * `kind`, which is where a marketplace side has always actually lived.
 *
 * `listSubmissionsForCurrentUser()` is gone rather than migrated. It had the
 * same missing filter and, despite its name, returned every submission on the
 * platform. It had zero call sites.
 */

import { apiDelete, apiGet, apiPut } from "./client";

export type OnboardingDraftRole = "coordinator" | "partner" | "hybrid";

export interface OnboardingDraft {
  role: string;
  step: string;
  data: Record<string, unknown>;
  updated_at: string;
}

export interface OnboardingContext {
  /** null when nothing has been saved yet. */
  draft: OnboardingDraft | null;
  /** null when the account has no membership yet — mid-registration. */
  company_id: string | null;
  /** What decides which wizard the user sees. Was `user.role`. */
  company_kind: "coordinator" | "partner" | "hybrid" | null;
}

/**
 * The draft and the wizard variant, in one call.
 *
 * They were two questions from two broken sources: a read that could not
 * return a row, and a column that no longer exists.
 */
export function fetchOnboardingContext(): Promise<OnboardingContext> {
  return apiGet<OnboardingContext>("/api/onboarding/context");
}

export function saveOnboardingDraft(args: {
  role: OnboardingDraftRole;
  step: string;
  data: Record<string, unknown>;
}): Promise<OnboardingDraft> {
  return apiPut<OnboardingDraft>("/api/onboarding/draft", args);
}

export function discardOnboardingDraft(): Promise<{ discarded: boolean }> {
  return apiDelete<{ discarded: boolean }>("/api/onboarding/draft");
}

/** The caller's own submissions, newest first. */
export function fetchOwnSubmissions(): Promise<Record<string, unknown>[]> {
  return apiGet<Record<string, unknown>[]>("/api/onboarding/submissions");
}
